import { useParams } from "react-router-dom";
import { NAV_MODULES } from "@/config/nav";
import { PageHeader, Panel } from "@/components/common";
import { Construction, Workflow } from "lucide-react";

const DETAILS = {
  scanner: { title: "Universal Market Scanner", desc: "Automated multi-market scanning that ranks instruments by GTF zone quality and trend alignment.", pipeline: ["Market scan", "Zone detection", "Signal rank"] },
  opportunities: { title: "Trading Opportunities", desc: "Qualified trade setups with entry, stop, target and reward-to-risk scoring.", pipeline: ["Signal", "Risk check", "Shortlist"] },
  gtf: { title: "GTF Strategy Center", desc: "The frozen 21-chapter GTF Trading in the Zone engine. Awaiting the complete book before any rule implementation.", pipeline: ["Candle class", "Zone logic", "MTF analysis", "Scoring"] },
  backtesting: { title: "Backtesting Laboratory", desc: "Historical replay with equity curves, win rate, profit factor and drawdown. No fabricated results.", pipeline: ["Select data", "Run strategy", "Analyze"] },
  paper: { title: "Paper Trading", desc: "Risk-free simulated execution against synthetic feeds.", pipeline: ["Signal", "Sim fill", "Track"] },
  live: { title: "Live Trading", desc: "Live automated execution. Permanently disabled in Phase 1.", pipeline: ["Scan", "Qualify", "Execute", "Manage"] },
  positions: { title: "Active Positions", desc: "Real-time open positions with unrealized P&L and protective stops.", pipeline: ["Monitor", "Trail", "Exit"] },
  orders: { title: "Order Management", desc: "Full order lifecycle: placement, modification, cancellation and fills.", pipeline: ["Place", "Modify", "Fill"] },
  compounding: { title: "Capital & Compounding", desc: "Reinvest realized profits, recalculate position sizing and manage reserves.", pipeline: ["Track", "Reinvest", "Resize"] },
  analytics: { title: "Performance Analytics", desc: "Deep performance reporting: returns, R-multiples, expectancy and attribution.", pipeline: ["Aggregate", "Analyze", "Report"] },
};

export default function UnderDevelopment() {
  const { "*": rest } = useParams();
  const key = (rest || "").split("/")[0] || window.location.pathname.split("/").pop();
  const mod = NAV_MODULES.find((m) => m.path.endsWith(key));
  const info = DETAILS[mod?.key] || {};
  const title = info.title || mod?.label || "Module";
  const Icon = mod?.icon || Construction;

  return (
    <div>
      <PageHeader title={title} subtitle="Phase 1 foundation · module scaffolded" icon={Icon} demo={false} testId="under-dev-header" />

      <Panel testId="under-dev-panel">
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5">
            <Construction className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-2">Under Development</h2>
          <p className="text-sm text-slate-500 max-w-lg mb-6">{info.desc || "This module is part of the Tradify roadmap. The navigation, layout and database foundations are in place; operational logic arrives in a later phase."}</p>

          {info.pipeline && (
            <div className="flex items-center gap-2 flex-wrap justify-center mb-6">
              {info.pipeline.map((step, i) => (
                <div key={step} className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-slate-400 bg-[var(--surface)] border border-[var(--border-subtle)] rounded-md px-3 py-1.5">{step}</span>
                  {i < info.pipeline.length - 1 && <Workflow className="w-3 h-3 text-slate-700" />}
                </div>
              ))}
            </div>
          )}

          <span className="text-[10px] font-mono text-slate-500 bg-slate-500/10 border border-slate-600/40 rounded px-3 py-1.5 uppercase tracking-wide">
            Not operational · No live trading
          </span>
        </div>
      </Panel>
    </div>
  );
}
